import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { retentionNotice, filesRemoved } from '@/lib/retentionNotice';
import { Upload, Flag, X, ChevronLeft, ChevronRight, ChevronDown, Download, AlertTriangle, MessageSquare, Send, Eye, Loader2, Maximize2, Trash2, Check, RefreshCw, Image as ImageIcon, SlidersHorizontal } from 'lucide-react';
import { MasonryPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/masonry.css';
import { useConfirm } from '@/components/ConfirmDialog';
import { FootageReportForm, FootageReportAdmin, FootageReportClient } from './FootageReport';
import SatisfactionSurvey from './SatisfactionSurvey';
import { ApproveAnywayDialog } from './ApproveAnywayDialog';
import { PhotoDeliverables } from './PhotoDeliverables';
import { SidecarPanel } from './SidecarPanel';
import { awaitingRelease } from '@/lib/draftGate';
import { EMPTY_DELIVERY, deliveryComplete, deliveryPayload } from '@/lib/editorTime';
import { DeliveryQuestions } from './DeliveryQuestions';
import { MustHavesPanel, AttestationModal, useFirstDraftChecklist } from './MustHaves';

const BATCH_SIZE = 6; // concurrent uploads
const THUMB_MAX = 800;   // thumb longest side (bumped 400→800 to stay crisp under the MasonryPhotoAlbum layout — commit 74c08081 render regression)
const THUMB_Q   = 0.82;  // JPEG quality (bumped 0.6→0.82 — same reason; still ~30× smaller than originals)
// Option 3 — server-side thumbnail regeneration UI toggle. New uploads now
// generate 800px/q0.82 thumbs natively, so the retrofit button is no longer
// needed in day-to-day ops. Kept the endpoints, handlers, and polling code
// intact — flip this back to `true` to restore the toolbar button instantly.
const SHOW_REGEN_THUMBS = false;

function generateThumbnail(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      if (w > h) { if (w > THUMB_MAX) { h = h * THUMB_MAX / w; w = THUMB_MAX; } }
      else { if (h > THUMB_MAX) { w = w * THUMB_MAX / h; h = THUMB_MAX; } }
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        resolve({ blob, width: img.width, height: img.height });
      }, 'image/jpeg', THUMB_Q);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

// Upload helpers removed — photo uploads now use api.post('/storage/upload/{file_id}') local proxy

// P1 shorted-delivery fix: paid deliverable expectation (bundle + overage),
// served as structured `project.expected_photos` to editor/admin/staff only —
// advisory display computed from image_count fields, never parsed from labels.
export function ExpectedPhotosChip({ project, testId = 'expected-photos-chip' }) {
  const ep = project?.expected_photos;
  if (project?.project_type !== 'photo' || !ep?.total) return null;
  const breakdown = ep.overage > 0 ? ` (${ep.bundle.toLocaleString()} bundle + ${ep.overage.toLocaleString()} overage)` : '';
  return (
    <span data-testid={testId}
      title="Deliverable photo count the client paid for (bundle + overage). Culling/retouch counts are inputs, not deliverables."
      className="tap-auto inline-flex items-center gap-1.5 bg-ug-gold/15 text-ug-gold border border-ug-gold/25 font-sans text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap">
      <ImageIcon className="w-3 h-3 shrink-0" />
      Expected photos: {ep.total.toLocaleString()}{breakdown}
    </span>
  );
}

function UploadZone({ projectId, version, onComplete, expected = 0, existingCount = 0 }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // When this upload starts a new version of a project WITHOUT the sidecar step, registering the set is
  // itself the delivery (backend create_photo_set), so an editor answers the two delivery questions first.
  // A sidecar project asks them at Submit Draft instead (SidecarPanel). The server decides which; this reads it.
  const opensVersion = user?.role === 'editor' && existingCount === 0;
  const [sidecarsRequired, setSidecarsRequired] = useState(null);
  useEffect(() => {
    if (!opensVersion) return;
    api.get(`/photo-review/${projectId}/sidecar-status?version=${version}`)
      .then(r => setSidecarsRequired(!!r.data?.required)).catch(() => setSidecarsRequired(false));
  }, [opensVersion, projectId, version]);
  const asksDelivery = opensVersion && sidecarsRequired === false;
  const deliveryPending = opensVersion && sidecarsRequired === null;
  const [delivery, setDelivery] = useState(EMPTY_DELIVERY);
  // The first-draft checklist (photo carries the notes confirmation alone), before the set that delivers.
  const checklist = useFirstDraftChecklist(projectId, asksDelivery);
  const [attesting, setAttesting] = useState(false);
  const needsChecklist = checklist.needed || checklist.pending;
  const blocked = deliveryPending || needsChecklist || (asksDelivery && !deliveryComplete(delivery));

  // Recursively read all files from dropped folders + files
  const getFilesFromDrop = async (dataTransfer) => {
    const files = [];
    const items = dataTransfer.items;
    if (items) {
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }
      const readEntry = (entry) => new Promise((resolve) => {
        if (entry.isFile) {
          entry.file(f => { if (f.type.startsWith('image/') || /\.(jpe?g|png|heic|heif|webp|tiff?)$/i.test(f.name)) files.push(f); resolve(); }, () => resolve());
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const readAll = () => {
            reader.readEntries(async (results) => {
              if (results.length === 0) { resolve(); return; }
              for (const r of results) await readEntry(r);
              readAll(); // readEntries returns max 100 at a time, must call again
            }, () => resolve());
          };
          readAll();
        } else { resolve(); }
      });
      for (const entry of entries) await readEntry(entry);
    }
    // Fallback if webkitGetAsEntry not supported
    if (files.length === 0) {
      for (const f of Array.from(dataTransfer.files)) {
        if (f.type.startsWith('image/') || /\.(jpe?g|png|heic|heif|webp|tiff?)$/i.test(f.name)) files.push(f);
      }
    }
    return files;
  };

  const handleFiles = async (files) => {
    if (blocked) {
      toast.error(needsChecklist ? 'Confirm the checklist before adding the photos' : 'Answer the two questions about this delivery first');
      return;
    }
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/') || /\.(jpe?g|png|heic|heif|webp|tiff?)$/i.test(f.name));
    if (!imageFiles.length) { toast.error('No image files found — supported formats: JPG, PNG, HEIC, WebP'); return; }
    setUploading(true);
    const total = imageFiles.length;
    setProgress({ done: 0, total });

    const allFiles = imageFiles.map(f => ({ file: f, filename: f.name, content_type: f.type || 'image/jpeg' }));
    const uploaded = [];
    let failCount = 0;

    for (let i = 0; i < allFiles.length; i += BATCH_SIZE) {
      const batch = allFiles.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async (f) => {
        try {
          const presign = await api.post('/storage/presign-upload', {
            filename: f.filename, scope: 'project_photo',
            project_id: projectId, version: String(version),
            content_type: f.content_type
          });
          if (presign.data.upload_url && presign.data.upload_url !== 'LOCAL') {
            // fetch() never throws on 4xx/5xx — check res.ok so a failed PUT doesn't
            // sail through as success and get confirmed as an empty "completed" file.
            const putRes = await fetch(presign.data.upload_url, { method: 'PUT', body: f.file, headers: { 'Content-Type': f.content_type || 'image/jpeg' } });
            if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
          } else {
            const formData = new FormData();
            formData.append('file', f.file);
            await api.post(`/storage/upload/${presign.data.file_id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
          await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
          // Generate thumbnail
          let thumbFileId = presign.data.file_id;
          let thumbR2Key = presign.data.key;
          let w = 0, h = 0;
          try {
            const thumb = await generateThumbnail(f.file);
            if (thumb) {
              const thumbPresign = await api.post('/storage/presign-upload', {
                filename: `thumb_${f.filename}`, scope: 'project_photo_thumb',
                project_id: projectId, version: String(version),
                content_type: 'image/jpeg'
              });
              if (thumbPresign.data.upload_url && thumbPresign.data.upload_url !== 'LOCAL') {
                const thumbPutRes = await fetch(thumbPresign.data.upload_url, { method: 'PUT', body: new File([thumb.blob], `thumb_${f.filename}`, { type: 'image/jpeg' }), headers: { 'Content-Type': 'image/jpeg' } });
                if (!thumbPutRes.ok) throw new Error(`Thumbnail upload failed (${thumbPutRes.status})`);
              } else {
                const thumbForm = new FormData();
                thumbForm.append('file', new File([thumb.blob], `thumb_${f.filename}`, { type: 'image/jpeg' }));
                await api.post(`/storage/upload/${thumbPresign.data.file_id}`, thumbForm, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
              }
              await api.post(`/storage/confirm-upload/${thumbPresign.data.file_id}`);
              thumbFileId = thumbPresign.data.file_id;
              thumbR2Key = thumbPresign.data.key;
              w = thumb.width; h = thumb.height;
            }
          } catch { /* thumb failed — use original as fallback */ }
          uploaded.push({
            photo_id: presign.data.file_id,
            filename: f.filename,
            r2_key: presign.data.key,
            thumb_r2_key: thumbR2Key,
            file_id: presign.data.file_id,
            thumb_file_id: thumbFileId,
            width: w, height: h
          });
        } catch (e) {
          failCount++;
        }
        setProgress(p => ({ ...p, done: p.done + 1 }));
      }));
    }

    // Register photos in the set
    if (uploaded.length > 0) {
      try {
        await api.post(`/photo-review/${projectId}/sets`,
          { version, photos: uploaded, ...(asksDelivery ? { delivery_answers: deliveryPayload(delivery) } : {}) });
        const msg = failCount > 0 ? `${uploaded.length} uploaded, ${failCount} failed` : `${uploaded.length} photo(s) uploaded`;
        toast.success(msg);
        setDelivery(EMPTY_DELIVERY);
        onComplete();
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to save photo set');
      }
    } else {
      toast.error(`Upload failed for all ${total} photos — please try again`);
    }
    setUploading(false);
  };

  return (
    <div className="space-y-3">
      {checklist.needed && (
        <MustHavesPanel projectId={projectId} gate={checklist.gate} onChanged={checklist.load} onConfirm={() => setAttesting(true)} />
      )}
      {attesting && checklist.gate && (
        <AttestationModal projectId={projectId} items={checklist.gate.items} onClose={() => setAttesting(false)}
          onComplete={(att) => { checklist.confirmed(att); setAttesting(false); }} />
      )}
      {asksDelivery && <DeliveryQuestions value={delivery} onChange={setDelivery} />}
      {asksDelivery && blocked && (
        <p className="font-sans text-[10px] text-slate-500" data-testid="photo-upload-delivery-hint">
          {checklist.needed ? 'Confirm the checklist above, then add the photos. Adding them delivers this version.'
            : 'Answer the two questions above, then add the photos. Adding them delivers this version.'}
        </p>
      )}
      {/* Shut, the zone still catches a drop (handleFiles refuses it with the reason): with no pointer events the
          photos would fall through to the browser, which opens them in place of the portal. */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-150 ${dragOver ? 'border-gold-leaf bg-gold-leaf/[0.06] scale-[1.01]' : 'border-ug-border hover:border-gold-leaf/30'} ${blocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !uploading && !blocked && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!blocked) setDragOver(true); }}
        onDragEnter={e => { e.preventDefault(); e.stopPropagation(); if (!blocked) setDragOver(true); }}
        onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
        onDrop={async e => { e.preventDefault(); e.stopPropagation(); setDragOver(false); const files = await getFilesFromDrop(e.dataTransfer); handleFiles(files); }}
        data-testid="photo-upload-zone"
      >
        {uploading ? (
          <div className="space-y-2">
            <Loader2 className="w-6 h-6 text-ug-gold animate-spin mx-auto" />
            <p className="font-sans text-[11px] font-medium text-ug-gold">Uploading {progress.done} / {progress.total}...</p>
            <div className="w-48 mx-auto h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gold-leaf rounded-full transition-all duration-300" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
            </div>
            {/* P3 shorted-delivery fix: display-only vs. the paid expectation — NEVER blocks submission */}
            {expected > 0 && (
              <p data-testid="submitting-vs-expected"
                className={`tap-auto text-[10px] font-medium ${(existingCount + progress.total) < expected ? 'text-amber-400' : 'text-slate-400'}`}>
                Submitting {(existingCount + progress.total).toLocaleString()} of {expected.toLocaleString()} expected
              </p>
            )}
            <p className="text-[9px] text-slate-600">Please don't close this panel</p>
          </div>
        ) : dragOver ? (
          <>
            <Upload className="w-6 h-6 text-ug-gold mx-auto mb-2" />
            <p className="text-[12px] text-ug-gold font-medium">Drop to upload</p>
            <p className="text-[9px] text-ug-gold/60 mt-1">Photos and folders supported</p>
          </>
        ) : (
          <>
            <Upload className="w-6 h-6 text-slate-500 mx-auto mb-2" />
            <p className="text-[12px] text-slate-400">Drop photos here or click to browse</p>
            <p className="text-[9px] text-slate-600 mt-1">Supports JPG, PNG, HEIC — folders and bulk upload supported</p>
          </>
        )}
      </div>
      <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif" className="hidden"
        onChange={e => handleFiles(Array.from(e.target.files || []))} />
    </div>
  );
}

function Lightbox({ photos, thumbUrls, startIndex, onClose, onFlag, isClient, notesLocked, onDelete, canDownload = true }) {
  const [idx, setIdx] = useState(startIndex);
  const [fullUrl, setFullUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const photo = photos[idx];
  const thumb = thumbUrls.find(t => t.photo_id === photo?.id);

  useEffect(() => { setIdx(startIndex); }, [startIndex]);
  useEffect(() => {
    if (!photo) return;
    setLoading(true);
    setNoteText(photo.note || '');
    // Always fetch the full-res original, never the compressed thumbnail
    const origFileId = thumb?.file_id || photo.file_id;
    if (origFileId) {
      // Local file — serve the ORIGINAL, not the thumbnail
      setFullUrl(`${api.defaults.baseURL}/storage/serve/${origFileId}`);
      setLoading(false);
    } else {
      api.post(`/photo-review/${photo.project_id || ''}/download-urls`, { photo_ids: [photo.id] })
        .then(r => { if (r.data[0]) setFullUrl(r.data[0].download_url); setLoading(false); })
        .catch(() => { setFullUrl(thumb?.thumb_url || ''); setLoading(false); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, photo?.id]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx(i => Math.min(i + 1, photos.length - 1));
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, photos.length]);

  if (!photo) return null;
  const imgSrc = fullUrl || thumb?.thumb_url;
  const downloadUrl = (thumb?.file_id || photo.file_id) ? `${api.defaults.baseURL}/storage/serve/${thumb?.file_id || photo.file_id}` : imgSrc;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/95 flex" data-testid="photo-lightbox">
      {/* Left: Image area with nav */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Close button */}
        <div className="flex items-center justify-between px-4 py-2 shrink-0">
          <span className="font-sans text-[10px] font-semibold text-white/40">{idx + 1} / {photos.length}</span>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20">
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Image + arrows */}
        <div className="flex-1 flex items-center justify-center relative px-12 min-h-0">
          <button onClick={() => setIdx(Math.max(idx - 1, 0))}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 z-10">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="max-h-full max-w-full flex items-center justify-center">
            {loading ? <Loader2 className="w-8 h-8 text-ug-gold animate-spin" /> : (
              <img src={imgSrc} alt={photo.filename} className="max-w-full max-h-[calc(100vh-120px)] object-contain rounded" />
            )}
          </div>
          <button onClick={() => setIdx(Math.min(idx + 1, photos.length - 1))}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 z-10">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        {/* Bottom bar: filename + download + delete */}
        <div className="flex items-center justify-center gap-4 py-2 shrink-0">
          <span className="font-sans text-[9px] font-semibold text-white/40 truncate max-w-[300px]">{photo.filename}</span>
          {canDownload && (
            <button onPointerUp={() => {
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.download = photo.filename || 'photo.jpg';
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }} className="flex items-center gap-1 text-[9px] text-slate-400 hover:text-ug-gold font-mono transition-colors cursor-pointer">
              <Download className="w-3.5 h-3.5" /> Download
            </button>
          )}
          {!isClient && onDelete && (
            <button onPointerUp={() => onDelete(photo.id, photo.filename)}
              className="flex items-center gap-1 text-[9px] text-slate-400 hover:text-velvet-burgundy font-mono transition-colors cursor-pointer">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Right: Notes panel (always visible) */}
      <div className="w-72 bg-ug-raised/80 border-l border-ug-border flex flex-col shrink-0" onClick={e => e.stopPropagation()}>
        <div className="p-4 flex-1 overflow-y-auto">
          <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">
            {isClient ? 'Photo Notes' : 'Client Notes'}
          </h3>
          {photo.flagged && (
            <Badge className="mb-2 bg-ug-status-red/10 text-ug-status-red border-0 font-sans text-[9px] font-semibold">Flagged for Revision</Badge>
          )}
          {isClient ? (
            <>
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)} disabled={notesLocked}
                placeholder={notesLocked ? "Notes locked" : "Describe what needs fixing..."} rows={4}
                className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2.5 text-sm resize-none placeholder:text-white/40 disabled:opacity-50"
                data-testid="photo-note-input" />
              {!notesLocked && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => onFlag(photo.id, true, noteText)} data-testid="flag-photo-btn"
                    className="flex-1 bg-ug-status-red/20 text-ug-status-red hover:bg-ug-status-red/30 font-sans text-[9px] font-semibold h-8">
                    <Flag className="w-3 h-3" /> Flag
                  </Button>
                  {photo.flagged && (
                    <Button size="sm" variant="outline" onClick={() => { onFlag(photo.id, false, ''); setNoteText(''); }}
                      className="border-ug-border text-slate-400 font-sans text-[9px] font-semibold h-8">Unflag</Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-[12px] text-zinc-100/70 leading-relaxed">{photo.note || 'No notes on this photo'}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function PhotoGrid({ photos, thumbUrls, onSelect, showFlaggedOnly, showRevisedOnly, isClient, maxPhotos, onFullscreen, onDeletePhoto }) {
  const visiblePhotos = showFlaggedOnly ? photos.filter(p => p.flagged) : showRevisedOnly ? photos.filter(p => p.revised) : photos;
  const displayPhotos = maxPhotos ? visiblePhotos.slice(0, maxPhotos) : visiblePhotos;
  const hasMore = maxPhotos && visiblePhotos.length > maxPhotos;

  // Build photo objects for react-photo-album
  const albumPhotos = displayPhotos.map((photo) => {
    const thumb = thumbUrls.find(t => t.photo_id === photo.id);
    const w = photo.width || thumb?.width || 400;
    const h = photo.height || thumb?.height || 300;
    // Grid tiles ALWAYS use thumbnails (~30x smaller). Lightbox fetches the full-res original separately.
    // Missing-thumb fallback: serve the original so the tile still renders (never blank).
    const origFileId = thumb?.file_id || photo.file_id;
    const src = thumb?.thumb_url || (origFileId ? `${api.defaults.baseURL}/storage/serve/${origFileId}` : '');
    return { src, width: w, height: h, key: photo.id, _photo: photo, _realIdx: photos.indexOf(photo) };
  }).filter(p => p.src);

  if (albumPhotos.length === 0) return <p className="text-center text-[11px] text-slate-600 py-4">No photos to display</p>;

  return (
    <div>
      <MasonryPhotoAlbum
        photos={albumPhotos}
        spacing={6}
        columns={(containerWidth) => {
          // Tighter tiles → tighter downscale of existing 400px thumbs → crisper
          // without needing regen. Sizes at common widths (with spacing=6 gaps):
          //   drawer @ 500 →  5 cols → ~94px tile   (2×DPR = ~188 device px)
          //   drawer @ 700 →  6 cols → ~110px tile  (2×DPR = ~220 device px)
          //   fullscr @ 1000→ 7 cols → ~137px tile  (2×DPR = ~274 device px)
          //   fullscr @ 1400→ 8 cols → ~168px tile  (2×DPR = ~336 device px)
          //   fullscr @ 1800→ 8 cols → ~217px tile  (2×DPR = ~434 device px)
          // All within/near the 400px source — no upscale on standard laptops.
          if (containerWidth < 400) return 4;
          if (containerWidth < 700) return 5;
          if (containerWidth < 1000) return 6;
          if (containerWidth < 1400) return 7;
          return 8;
        }}
        render={{
          photo: (_, { photo, width, height }) => {
            const p = photo._photo;
            if (!p) return null;
            return (
              <button key={p.id} onClick={() => onSelect(photo._realIdx)}
                data-testid={`photo-tile-${p.id}`}
                className="relative rounded overflow-hidden group cursor-pointer block w-full">
                <img src={photo.src} width={width} height={height} alt={p.filename} className="w-full h-auto block rounded transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" />
                {p.flagged && (
                  <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-ug-status-red flex items-center justify-center">
                    <Flag className="w-3 h-3 text-white" />
                  </div>
                )}
                {p.revised && !p.flagged && (
                  <div className="absolute top-1 right-1 bg-oxidized-teal/90 text-white font-mono text-[7px] px-1.5 py-0.5 rounded">
                    Revised
                  </div>
                )}
                {p.note && !p.flagged && (
                  <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gold-leaf/80 flex items-center justify-center">
                    <MessageSquare className="w-3 h-3 text-obsidian" />
                  </div>
                )}
                {!isClient && onDeletePhoto && (
                  <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span onPointerUp={(e) => { e.stopPropagation(); onDeletePhoto(p.id, p.filename); }}
                      className="w-5 h-5 rounded-full bg-black/60 flex items-center justify-center cursor-pointer hover:bg-velvet-burgundy/80 transition-colors">
                      <Trash2 className="w-3 h-3 text-white" />
                    </span>
                  </div>
                )}
              </button>
            );
          }
        }}
      />
      {hasMore && (
        <div className="text-center py-6 mt-3 border-t border-ug-border">
          <p className="font-sans text-xs text-ug-gold font-medium mb-1">
            {visiblePhotos.length - maxPhotos} more photos
          </p>
          <p className="text-[11px] text-slate-400 mb-4">
            Open Full Screen to view all photos, leave notes, and request revisions or approve
          </p>
          {onFullscreen && (
            <button onClick={onFullscreen}
              className="px-6 py-2.5 rounded-md bg-gold-leaf/10 border border-gold-leaf/30 text-ug-gold font-sans text-[11px] font-medium uppercase tracking-wider hover:bg-gold-leaf/20 transition-colors inline-flex items-center gap-2">
              <Maximize2 className="w-4 h-4" /> View All in Full Screen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PhotoReview({ project, onRefresh, onFullscreen }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [photoSet, setPhotoSet] = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [thumbUrls, setThumbUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [generalNote, setGeneralNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(true); // true = read-only, false = editing
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [showRevisedOnly, setShowRevisedOnly] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [xmpDownloading, setXmpDownloading] = useState(false);
  const [footageReport, setFootageReport] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [showApproveAnyway, setShowApproveAnyway] = useState(false);
  const [revNotesOpen, setRevNotesOpen] = useState(false);
  // Option 3 — server-side thumbnail regeneration (admin/staff)
  const [regenStatus, setRegenStatus] = useState(null); // { status, processed, skipped, total, errors, spec, started_at, finished_at }
  const [regenStarting, setRegenStarting] = useState(false);
  const regenPollTimerRef = useRef(null);

  const isClient = user.role === 'client';
  // A1 approve-to-unlock: client full-res downloads wait for approval (viewing stays open)
  const canDownload = !isClient || !!project.client_approved || !!project.photo_download_pre_approval_ok;
  const isUploader = user.role === 'admin' || user.role === 'staff' || user.role === 'editor';
  const isAdmin = user.role === 'admin' || user.role === 'staff';
  const photos = photoSet?.photos || [];
  const flaggedCount = photos.filter(p => p.flagged).length;
  const revisedCount = photos.filter(p => p.revised).length;
  const notesLocked = photoSet?.notes_locked || false;
  const revisionRequested = photoSet?.revision_requested || false;
  const version = photoSet?.version || 1;

  const { files_recovery_state: recoveryState, files_purged: filesPurged } = project;
  const fetchData = useCallback(async (isInitial = false) => {
    if (filesRemoved({ files_recovery_state: recoveryState, files_purged: filesPurged })) { setPhotoSet(null); setThumbUrls([]); setLoading(false); return; }
    if (isInitial) setLoading(true);
    try {
      const res = await api.get(`/photo-review/${project.id}/latest`);
      if (res.data && res.data.id) {
        setPhotoSet(res.data);
        setGeneralNote(res.data.general_note || '');
        setNoteSaved(!!(res.data.general_note));
        const thumbs = await api.post(`/photo-review/${project.id}/thumb-urls`, { version: res.data.version });
        setThumbUrls(thumbs.data);
        api.get(`/photo-review/${project.id}`).then(r => setAllVersions(r.data || [])).catch(() => {});
      } else {
        setPhotoSet(null);
        setThumbUrls([]);
      }
    } catch { setPhotoSet(null); setThumbUrls([]); }
    setLoading(false);
  }, [project.id, recoveryState, filesPurged]);

  // Fetch footage report
  useEffect(() => {
    if (project.id) {
      api.get(`/projects/${project.id}/footage-report`).then(r => setFootageReport(r.data?.report || null)).catch(() => {});
    }
  }, [project.id]);

  // Fetch on mount AND when project changes
  const prevProjectId = useRef(null);
  useEffect(() => {
    if (prevProjectId.current !== project.id) {
      prevProjectId.current = project.id;
      setPhotoSet(null);
      setThumbUrls([]);
      setAllVersions([]);
      setLightboxIdx(-1);
      fetchData(true);
    }
  }, [project.id, fetchData]);

  // Option 3 — poll thumbnail-regen status; auto-refresh grid when it finishes.
  useEffect(() => {
    if (!isAdmin || !photoSet?.id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/photo-review/${project.id}/regenerate-thumbnails/status?version=${photoSet.version}`);
        if (cancelled) return;
        setRegenStatus(r.data);
        if (r.data?.status === 'running') {
          regenPollTimerRef.current = setTimeout(poll, 1500);
        } else if (r.data?.status === 'done' || r.data?.status === 'failed') {
          // one-shot: bust the thumb URL cache by re-fetching signed thumb URLs
          fetchData(false);
        }
      } catch { /* silent — status endpoint is best-effort */ }
    };
    // Initial fetch on mount / version switch so we know current state.
    poll();
    return () => {
      cancelled = true;
      if (regenPollTimerRef.current) clearTimeout(regenPollTimerRef.current);
    };
  }, [project.id, photoSet?.id, photoSet?.version, isAdmin, fetchData]);

  const startThumbnailRegen = async () => {
    const ok = await confirm(
      `Regenerate all ${photos.length.toLocaleString()} thumbnails at the new higher quality (800px, q=82)? Originals are untouched — only the thumbnail files are overwritten. Runs in the background; you can close this page.`,
      { title: 'Regenerate thumbnails?', confirmLabel: 'Regenerate' }
    );
    if (!ok) return;
    setRegenStarting(true);
    try {
      const r = await api.post(`/photo-review/${project.id}/regenerate-thumbnails?version=${version}`);
      toast.success(`Regenerating ${r.data.total} thumbnails at ${r.data.spec}…`);
      setRegenStatus({ status: 'running', total: r.data.total, processed: 0, skipped: 0, spec: r.data.spec });
      // Kick off polling immediately (the effect above will pick up on state change).
      const poll = async () => {
        try {
          const s = await api.get(`/photo-review/${project.id}/regenerate-thumbnails/status?version=${version}`);
          setRegenStatus(s.data);
          if (s.data?.status === 'running') {
            regenPollTimerRef.current = setTimeout(poll, 1500);
          } else {
            fetchData(false);
          }
        } catch { /* silent */ }
      };
      regenPollTimerRef.current = setTimeout(poll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start regeneration');
    } finally {
      setRegenStarting(false);
    }
  };

  const handleFlag = async (photoId, flagged, note) => {
    try {
      await api.put(`/photo-review/${project.id}/photos/${photoId}/flag`, { flagged, note });
      setPhotoSet(prev => {
        if (!prev) return prev;
        return { ...prev, photos: prev.photos.map(p => p.id === photoId ? { ...p, flagged, note: flagged ? note : '' } : p) };
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSaveGeneralNote = async () => {
    try {
      await api.put(`/photo-review/${project.id}/general-note`, { note: generalNote });
      setNoteSaved(true);
      toast.success('Note saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const switchVersion = async (v) => {
    setLoading(true);
    try {
      const sets = allVersions.length > 0 ? allVersions : (await api.get(`/photo-review/${project.id}`)).data;
      const ps = sets.find(s => s.version === v);
      if (ps) {
        setPhotoSet(ps);
        setGeneralNote(ps.general_note || '');
        setNoteSaved(!!(ps.general_note));
        const thumbs = await api.post(`/photo-review/${project.id}/thumb-urls`, { version: v });
        setThumbUrls(thumbs.data);
      }
    } catch { toast.error('Failed to load version'); }
    setLoading(false);
  };

  const handleRequestRevision = async () => {
    if (!flaggedCount && !generalNote.trim()) {
      toast.error('Flag at least one photo or add a general note before requesting revision');
      return;
    }
    try {
      const res = await api.post(`/photo-review/${project.id}/request-revision`);
      toast.success(`Revision requested — ${res.data.flagged_count} photo(s) flagged`);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [zipProgress, setZipProgress] = useState(null); // {done, total} or null

  // Turn any thrown error into something a person can act on. The previous
  // `catch { toast.error('Download failed') }` rendered every distinct failure —
  // expired job, missing archive, 403 gate, dropped connection — as the same
  // three words, which is why a real client report could not be diagnosed from
  // either end. Keep the server's own message when it sent one.
  const describeError = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string' && detail) return detail;
    if (e?.response?.status) return `${fallback} (HTTP ${e.response.status})`;
    if (e?.message) return `${fallback} — ${e.message}`;
    return fallback;
  };

  // Same job lifecycle as the photo ZIP — the server registers it in the same
  // registry, so progress and delivery are the identical endpoints.
  const handleDownloadXmp = async () => {
    setXmpDownloading(true);
    setZipProgress({ done: 0, total: photos.length });
    try {
      const startRes = await api.post(`/photo-review/${project.id}/start-xmp-zip`, {});
      const { job_id } = startRes.data;
      let status = 'working';
      let failedFiles = [];
      let pollErrors = 0;
      while (status === 'working') {
        await new Promise(r => setTimeout(r, 500));
        try {
          const prog = await api.get(`/photo-review/${project.id}/zip-progress/${job_id}`);
          pollErrors = 0;
          setZipProgress({ done: prog.data.done, total: prog.data.total });
          failedFiles = prog.data.failed_files || [];
          status = prog.data.status;
          if (status === 'error') throw new Error(prog.data.error || 'Could not build the edit settings');
        } catch (e) {
          if (e?.response?.status === 404 || !e?.response || ++pollErrors > 5) throw e;
        }
      }
      const link = document.createElement('a');
      link.href = `${api.defaults.baseURL}/photo-review/${project.id}/download-zip/${job_id}`;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      const missing = failedFiles.length;
      toast.success(`Edit settings ready — ${photos.length - missing} sidecar${photos.length - missing !== 1 ? 's' : ''}`, {
        description: missing
          ? `${missing} photo${missing !== 1 ? 's' : ''} had no embedded settings. Drop the .xmp files beside your raw files and Lightroom will pick them up.`
          : 'Drop the .xmp files beside your raw files and Lightroom will pick them up.',
        duration: 10000,
      });
    } catch (e) {
      console.error('[xmp download] failed', e?.response?.status, e?.response?.data || e);
      toast.error(describeError(e, 'Could not prepare the edit settings'));
    }
    setXmpDownloading(false);
    setZipProgress(null);
  };

  // The archive is now streamed straight from storage, so there is nothing to
  // prepare and nothing to poll: the browser starts saving on the first byte and
  // shows its own progress. That matters most on the galleries this had to be
  // fixed for — 9GB and 15.7GB — where the old path had to build the whole thing
  // on the server first and needed twice the gallery in disk to do it.
  //
  // Photos that cannot be read no longer surface as a toast, because the server
  // does not know they are missing until it reaches them mid-stream. They are
  // listed in a _MISSING_FILES.txt entry inside the archive instead, so a short
  // download still cannot pass as a complete one.
  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      // No preflight: the two ways this can be refused are already prevented in
      // the UI — `canDownload` gates an unapproved client, and the button only
      // renders for a non-empty set.
      const link = document.createElement('a');
      link.href = `${api.defaults.baseURL}/photo-review/${project.id}/download-gallery?version=${version || 0}`;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Downloading ${photos.length} photos`, {
        description: 'Your browser will show the progress. Large galleries can take a while.',
        duration: 8000,
      });
    } catch (e) {
      console.error('[photo download] failed', e?.response?.status, e?.response?.data || e);
      toast.error(describeError(e, 'Download failed'));
    }
    setDownloading(false);
  };

  if (loading) return <div className="p-5 text-center font-sans text-xs font-medium text-slate-500">Loading photos...</div>;

  // Photos gone under the retention policy: the notice where the grid was, for every role, and
  // no thumbnail, lightbox or download that could 410. lib/retentionNotice.js owns the words.
  const removed = retentionNotice(project);
  if (removed) {
    return (
      <div className="p-5" data-testid="photos-removed-panel" data-state={removed.state}>
        <div className="p-4 rounded-lg bg-ug-status-red/[0.06] border border-ug-status-red/20 text-center">
          <ImageIcon className="w-8 h-8 text-ug-status-red/40 mx-auto mb-2" />
          <p className="font-sans text-[11px] font-semibold text-ug-status-red">{removed.state === 'gone' ? 'Photos removed' : 'Photos removed, can be brought back'}</p>
          <p className="font-sans text-[10px] text-zinc-400 mt-1 leading-relaxed">{removed.text}</p>
        </div>
      </div>
    );
  }

  // No photos yet
  if (!photoSet || photos.length === 0) {
    return (
      <div className="p-5 space-y-4">
        {isUploader && (
          <>
            <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Upload Photos</h3>
            <ExpectedPhotosChip project={project} testId="expected-photos-chip-photos-tab" />
            <UploadZone projectId={project.id} version={1} onComplete={fetchData}
              expected={project.expected_photos?.total || 0} existingCount={0} />
          </>
        )}
        {isClient && (
          <div className="text-center py-12">
            <p className="text-[12px] text-slate-500">No photos uploaded yet</p>
            <p className="text-[10px] text-slate-600 mt-1">Your edited photos will appear here once uploaded by the team</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4">
      {/* Revision Request Notes — thin red collapsible banner for admin/editor */}
      {isUploader && project.revision_request_notes && (
        <button onClick={() => setRevNotesOpen(o => !o)} data-testid="revision-notes-banner"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-[#F97066]/[0.08] border border-[#F97066]/25 hover:bg-[#F97066]/[0.12] transition-colors text-left">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F97066] shrink-0" />
          <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-[#F97066] flex-1">Client Revision Notes</span>
          <ChevronDown className={`w-3 h-3 text-[#F97066]/60 transition-transform ${revNotesOpen ? 'rotate-180' : ''}`} />
        </button>
      )}
      {isUploader && project.revision_request_notes && revNotesOpen && (
        <div className="px-3 py-2.5 rounded-b-md -mt-2 bg-[#F97066]/[0.04] border border-t-0 border-[#F97066]/15" data-testid="revision-notes-expanded">
          {project.revisions_requested_at && (
            <span className="font-sans text-[8px] text-slate-500 block mb-1">{new Date(project.revisions_requested_at).toLocaleString()}</span>
          )}
          <p className="text-[12px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed">{project.revision_request_notes}</p>
        </div>
      )}

      {/* Header: stats + actions.
          gap-x/gap-y split: the row wraps when the drawer is narrow, and a
          symmetric gap-2 then put as much space between the two stacked rows as
          between neighbouring buttons, which read as dead vertical padding. */}
      <div className="flex items-start justify-between flex-wrap gap-x-2 gap-y-1">
        {/* NO flex-1 here: the stats block growing to fill the line squeezed the
            action buttons into the leftover sliver, wrapping them onto two rows
            while dead space sat between stats and buttons. Natural width + the
            parent's justify-between keeps all four buttons on one line whenever
            they genuinely fit. */}
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">
            Photos <span className="text-slate-500">v{version}</span>
          </h3>
          {allVersions.length > 1 && (
            <div className="flex gap-1">
              {allVersions.map(v => (
                <button key={v.version} onClick={() => switchVersion(v.version)}
                  className={`font-sans text-[9px] font-semibold px-2 py-0.5 rounded ${v.version === version ? 'bg-gold-leaf/20 text-ug-gold' : 'text-slate-500 hover:text-slate-300'}`}>
                  V{v.version}
                </button>
              ))}
            </div>
          )}
          {/* nowrap: "700 photos" was breaking between the count and the word once
              the toolbar filled up, stacking it over two rows. */}
          <span className="text-[10px] text-slate-500 whitespace-nowrap">{photos.length} photos</span>
          {!isClient && <ExpectedPhotosChip project={project} testId="expected-photos-chip-photos-tab" />}
          {/* P3: delivered-vs-expected — advisory only, never gates anything */}
          {!isClient && project.expected_photos?.total > 0 && photos.length > 0 && (
            <span data-testid="photo-count-vs-expected"
              title="Photos in this set vs. the paid deliverable expectation (bundle + overage). Advisory only."
              className={`tap-auto font-sans text-[9px] font-semibold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${photos.length < project.expected_photos.total ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-white/[0.04] text-slate-400 border-ug-border'}`}>
              {photos.length.toLocaleString()} of {project.expected_photos.total.toLocaleString()} expected
            </span>
          )}
          {flaggedCount > 0 && (
            <Badge className="bg-ug-status-red/10 text-ug-status-red border-0 font-sans text-[9px] font-semibold">
              {flaggedCount} flagged
            </Badge>
          )}
          {revisedCount > 0 && (
            <Badge className="bg-oxidized-teal/10 text-oxidized-teal border-0 font-sans text-[9px] font-semibold">
              {revisedCount} revised
            </Badge>
          )}
          {revisionRequested && (
            <Badge className="bg-gold-leaf/10 text-ug-gold border-0 font-sans text-[9px] font-semibold">Revision Requested</Badge>
          )}
        </div>
        {/* The buttons and the progress indicator are SEPARATE rows now. They used
            to share one flex line, so the moment a download started the progress
            bar took horizontal space and every button shrank — labels wrapped
            mid-word and "FULL SCREEN" stacked over three lines. shrink-0 on the
            buttons plus the indicator on its own line means the toolbar looks
            identical whether or not a download is running. */}
        {/* w-full sm:w-auto + wrap: four shrink-0 buttons can exceed a narrow
            (client/mobile) drawer's width, and with justify-end the OVERFLOW
            hangs out the LEFT edge past the container padding — "Download All
            off to the side". Full-width + wrap + left-aligned on narrow keeps
            every button inside the padded box; desktop stays right-aligned. */}
        <div className="flex flex-col items-stretch sm:items-end gap-1 shrink-0 w-full sm:w-auto">
        <div className="flex items-center gap-1.5 flex-wrap justify-start sm:justify-end">
          {(isClient || isUploader) && (
            <>
              {canDownload ? (
                <>
                <Button size="sm" variant="outline" onClick={handleDownloadAll} disabled={downloading}
                  data-testid="download-all-btn"
                  className="tap-auto h-7 gap-1 px-2 shrink-0 font-sans text-[9px] font-semibold border-ug-border text-slate-400 hover:text-zinc-100">
                  <Download className="w-3 h-3" /> {downloading ? 'Preparing...' : 'Download All'}
                </Button>
                {/* The develop settings ride along inside every delivered JPEG; this
                    just lifts them out as sidecars. Separate from Download All because
                    it answers a different need — re-editing from your own raw files —
                    and is ~1.5MB rather than several GB. */}
                <Button size="sm" variant="outline" onClick={handleDownloadXmp} disabled={xmpDownloading}
                  data-testid="download-xmp-btn"
                  title="Download the Lightroom edit settings as .xmp sidecars, to apply these edits to your own raw files"
                  className="tap-auto h-7 gap-1 px-2 shrink-0 font-sans text-[9px] font-semibold border-ug-border text-slate-400 hover:text-zinc-100">
                  <SlidersHorizontal className="w-3 h-3" /> {xmpDownloading ? 'Preparing...' : 'Edit Settings'}
                </Button>
                </>
              ) : (
                <span data-testid="download-locked-hint" title="Approve your photos to unlock the full-resolution download"
                  className="tap-auto inline-flex items-center gap-1 h-7 px-2 rounded border border-ug-border/60 font-sans text-[9px] font-medium text-slate-500 cursor-default">
                  <Download className="w-3 h-3 opacity-50" /> Downloads unlock after approval
                </span>
              )}
            </>
          )}
          {photos.length > 0 && (
            <Button size="sm" variant={showFlaggedOnly ? 'default' : 'outline'} onClick={() => { setShowFlaggedOnly(!showFlaggedOnly); setShowRevisedOnly(false); }}
              data-testid="filter-flagged-btn"
              className={`tap-auto h-7 gap-1 px-2 shrink-0 font-sans text-[9px] font-semibold ${showFlaggedOnly ? 'bg-ug-status-red/20 text-ug-status-red border-0' : 'border-ug-border text-slate-400'}`}>
              <Flag className="w-3 h-3" /> {showFlaggedOnly ? 'Show All' : `Flagged (${flaggedCount})`}
            </Button>
          )}
          {revisedCount > 0 && (
            <Button size="sm" variant={showRevisedOnly ? 'default' : 'outline'} onClick={() => { setShowRevisedOnly(!showRevisedOnly); setShowFlaggedOnly(false); }}
              data-testid="filter-revised-btn"
              className={`tap-auto h-7 gap-1 px-2 shrink-0 font-sans text-[9px] font-semibold ${showRevisedOnly ? 'bg-oxidized-teal/20 text-oxidized-teal border-0' : 'border-ug-border text-slate-400'}`}>
              Revised ({revisedCount})
            </Button>
          )}
          {onFullscreen && (
            <button onClick={onFullscreen} data-testid="expand-photos-btn"
              title="Full screen" aria-label="Full screen"
              className="tap-auto flex items-center gap-1 px-2 h-7 shrink-0 whitespace-nowrap rounded-md text-[9px] font-sans font-semibold uppercase tracking-wide text-slate-400 hover:text-ug-gold hover:bg-white/5 transition-colors border border-ug-border">
              <Maximize2 className="w-3 h-3" />
              {/* Icon-only below sm: four labelled buttons cannot fit one row on
                  a phone, and the operator requires ONE row — the expand glyph
                  is self-evident and title/aria keep it accessible. */}
              <span className="hidden sm:inline">Full Screen</span>
            </button>
          )}
          {/* Option 3 — admin regenerate thumbnails (retrofits old 400px/q0.6 thumbs → 800px/q0.82).
              Currently HIDDEN via SHOW_REGEN_THUMBS flag at top of file; endpoints + click/poll
              handlers remain wired so flipping the flag to `true` restores the button unchanged. */}
          {SHOW_REGEN_THUMBS && isAdmin && photos.length > 0 && (
            <>
              {regenStatus?.status === 'running' ? (
                <div className="flex items-center gap-2 px-3 h-7 rounded-md border border-gold-leaf/30 bg-gold-leaf/5" data-testid="regen-progress">
                  <Loader2 className="w-3 h-3 animate-spin text-ug-gold" />
                  <div className="w-20 h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-ug-gold rounded-full transition-all" style={{ width: `${regenStatus.total ? ((regenStatus.processed||0)+(regenStatus.skipped||0))/regenStatus.total*100 : 0}%` }} />
                  </div>
                  <span className="font-sans text-[9px] font-medium text-ug-gold/90">{(regenStatus.processed||0)+(regenStatus.skipped||0)}/{regenStatus.total}</span>
                </div>
              ) : (
                <button type="button" onClick={startThumbnailRegen} disabled={regenStarting}
                  data-testid="regen-thumbnails-btn"
                  title="Regenerate all thumbnails at the new 800px/q0.82 spec. Originals are untouched — only thumb files are overwritten. Runs in the background."
                  className="tap-auto flex items-center gap-1.5 px-3 h-7 rounded-md text-[9px] font-mono uppercase tracking-wider text-slate-400 hover:text-ug-gold hover:bg-white/5 transition-colors border border-ug-border disabled:opacity-50">
                  <RefreshCw className={`w-3 h-3 ${regenStarting ? 'animate-spin' : ''}`} /> Regen Thumbs
                  {regenStatus?.status === 'done' && (regenStatus.skipped || 0) === 0 && (
                    <span className="text-oxidized-teal">✓</span>
                  )}
                  {regenStatus?.status === 'done' && (regenStatus.skipped || 0) > 0 && (
                    <span className="text-amber-400" title={`${regenStatus.skipped} skipped — see console`}>({regenStatus.skipped} skipped)</span>
                  )}
                  {regenStatus?.status === 'failed' && (
                    <span className="text-velvet-burgundy">failed</span>
                  )}
                </button>
              )}
            </>
          )}
          {isUploader && version > 0 && !project.client_approved && (
            <button type="button" onPointerUp={async (e) => {
              e.stopPropagation(); e.preventDefault();
              const ok = await confirm(`This will permanently delete Photo Gallery V${version} and all its photos.`, { title: `Delete Gallery V${version}?`, confirmLabel: 'Delete Gallery' });
              if (!ok) return;
              try { await api.delete(`/photo-review/${project.id}/versions/${version}`); toast.success('Gallery version deleted'); fetchData(true); } catch { toast.error('Failed to delete'); }
            }} className="tap-auto flex items-center justify-center w-7 h-7 shrink-0 rounded-md text-velvet-burgundy hover:bg-velvet-burgundy/10 transition-colors border border-ug-border" data-testid="delete-photo-version-btn">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        {/* Progress on its OWN line, under the buttons: sharing the row is what
            squashed them mid-download. */}
        {zipProgress && (
          <div className="flex items-center gap-2 justify-end" data-testid="zip-progress">
            <div className="w-32 h-1.5 bg-white/5 rounded-full overflow-hidden shrink-0">
              <div className="h-full bg-gold-leaf rounded-full transition-all duration-300"
                style={{ width: `${zipProgress.total ? (zipProgress.done / zipProgress.total) * 100 : 0}%` }} />
            </div>
            {/* nowrap + tabular-nums: "0/700" was breaking mid-number into "0/70"
                over "0", and the digits jittered as the count rose. */}
            <span className="font-sans text-[8px] font-medium text-ug-gold/70 whitespace-nowrap tabular-nums"
              data-testid="zip-progress-count">
              {zipProgress.phase === 'sizing' ? 'sizing…' : `${zipProgress.done}/${zipProgress.total}`}
            </span>
          </div>
        )}
        </div>
      </div>

      {/* Admin/Editor: ordered photo count info */}
      {isUploader && (() => {
        const orderedCount = project.desired_edit_count || project.submitted_image_count || 0;
        if (!orderedCount) return null;
        const uploadedCount = photos.length;
        const under = orderedCount > uploadedCount;
        return (
          <div className={`flex items-center gap-2 text-[10px] font-mono ${under ? 'text-ug-gold' : 'text-slate-500'}`}>
            {under && <AlertTriangle className="w-3 h-3 shrink-0" />}
            <span>Client ordered {orderedCount} photos — {uploadedCount} uploaded{under ? ` (${orderedCount - uploadedCount} remaining)` : ''}</span>
          </div>
        );
      })()}

      {/* Upload zone for admin/editor */}
      {isUploader && (
        <>
          {revisionRequested && (
            <div className="p-3 rounded-md bg-ug-gold/8 border border-ug-gold/15">
              <p className="text-[11px] text-ug-gold font-medium">Upload V{version + 1} — Revised Photos</p>
              <p className="text-[9px] text-ug-gold/70 mt-1">Important: New version exports <span className="text-ug-gold font-medium">must use exactly the same filenames</span> as the previous version. This allows the system to automatically identify which photos were revised for the client.</p>
            </div>
          )}
          <UploadZone projectId={project.id} version={revisionRequested ? version + 1 : version} onComplete={fetchData}
            expected={project.expected_photos?.total || 0} existingCount={revisionRequested ? 0 : photos.length} />
        </>
      )}

      {/* Admin: Approve & Send to Client — mirrors VideoReview.js, same gating,
          calls the shared PUT /projects/{id}/approve-draft endpoint (no photo-only path). */}
      {isAdmin && awaitingRelease(project) && (
        <button onClick={async () => {
          try { await api.put(`/projects/${project.id}/approve-draft`); toast.success('Approved — client can now see it'); onRefresh && onRefresh(); } catch { toast.error('Failed'); }
        }} data-testid="approve-draft-btn"
          className="w-full p-3 rounded-md bg-gold-leaf/[0.06] border border-gold-leaf/25 text-ug-gold font-sans text-[10px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/10 transition-colors flex items-center justify-center gap-2">
          <Check className="w-4 h-4" /> Approve &amp; Send to Client
        </button>
      )}

      {/* Source/working deliverables (Lightroom catalog, RAW, sidecars). Uploader can
          attach/remove; client sees them listed once the draft is released and can
          download only after they approve (client_approved) — parity with video files. */}
      {/* Gate: a photo draft cannot be submitted until every delivered photo
          has its develop settings attached. Sits directly above Source Files
          because that is where the editor already comes to hand work over. */}
      <SidecarPanel project={project} version={photoSet?.version || 1}
        photoCount={photos.length} onSubmitted={() => { fetchData(); onRefresh && onRefresh(); }} />
      <PhotoDeliverables project={project} onRefresh={onRefresh} />

      {/* General note + action buttons — notes on top, buttons below */}
      {isClient && (
        <div className="pt-3 space-y-3" data-testid="general-note-section">
          {/* Client read-back of their OWN submitted revision request (display-only).
              Mirrors VideoReview.js — persists whenever revision_request_notes exists
              (BOTH while revisions_requested=true AND after a new version lands), so the
              photo client can always see what they previously asked for. Labeled as a
              RECORD ("Your revision request · sent {date}"), never a prompt. Reads the
              existing project payload field — no write/storage change; the staff red
              dropdown above stays uploader-only. */}
          {project.revision_request_notes && project.revision_request_notes.trim() && (
            <div
              data-testid="client-revision-request-readback"
              className="p-3 rounded-md bg-[#F97066]/[0.06] border border-[#F97066]/25">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F97066] shrink-0" />
                <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-[#F97066]">
                  Your revision request
                </span>
                {project.revisions_requested_at && (
                  <span className="font-sans text-[8px] text-slate-500 normal-case tracking-normal">
                    · sent {new Date(project.revisions_requested_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              <p className="text-[12px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed">{project.revision_request_notes}</p>
            </div>
          )}
          {/* Notes area — full width */}
          <div className="space-y-1">
            <h4 className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">General Notes</h4>
            {!notesLocked && !noteSaved ? (
              /* Save sits BELOW the textarea, not floated inside it — the old
                 absolute-positioned button overlapped the text and, lacking
                 `.tap-auto`, was inflated to 44px by the global testid rule
                 (occurrence #8 of that trap), which is why it looked broken. */
              <div>
                <textarea value={generalNote} onChange={e => setGeneralNote(e.target.value)}
                  placeholder="Overall feedback for all photos (e.g. 'Too warm overall, prefer cooler tones')"
                  className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2.5 text-sm resize-none h-20 placeholder:text-white/40 focus:border-gold-leaf/40 outline-none"
                  data-testid="general-note-input" />
                <div className="flex justify-end mt-1 mb-1">
                  <button onClick={handleSaveGeneralNote} data-testid="save-general-note-btn"
                    className="tap-auto px-3 py-1.5 rounded-md bg-gold-leaf text-obsidian font-sans text-[10px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/85 transition-colors">
                    Save note
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-2.5 rounded bg-ug-surface/50 border border-ug-border-subtle min-h-[40px] flex items-center justify-between group">
                <p className="text-[11px] text-zinc-100/70 leading-relaxed flex-1">{generalNote || 'No general notes yet'}</p>
                {!notesLocked && (
                  <button onClick={() => setNoteSaved(false)} className="text-[9px] text-slate-500 hover:text-ug-gold font-mono ml-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">Edit</button>
                )}
              </div>
            )}
          </div>
          {/* Action buttons — full width, stacked on mobile */}
          {photos.length > 0 && !project.client_approved && !project.revisions_requested && !notesLocked && (
            <div className="space-y-2 pt-1">
              <div className="flex flex-col sm:flex-row gap-2">
                <button onClick={handleRequestRevision} data-testid="request-revision-btn"
                  className="flex-1 py-2.5 rounded-md bg-velvet-burgundy/80 hover:bg-velvet-burgundy text-white text-[12px] font-medium tracking-wide transition-colors text-center">
                  Request Revisions {flaggedCount > 0 ? `(${flaggedCount})` : ''}
                </button>
                {!showSurvey && (
                  <button onClick={() => setShowSurvey(true)} data-testid="approve-project-btn"
                    className="flex-1 py-2.5 rounded-md bg-oxidized-teal/80 hover:bg-oxidized-teal text-white text-[12px] font-medium tracking-wide transition-colors text-center">
                    Approve Project
                  </button>
                )}
              </div>
              {showSurvey && (
                <div className="p-4 rounded-xl bg-ug-raised border border-ug-border">
                  <SatisfactionSurvey
                    submitting={surveySubmitting}
                    onCancel={() => setShowSurvey(false)}
                    onSubmit={async (satisfaction) => {
                      setSurveySubmitting(true);
                      try {
                        await api.put(`/projects/${project.id}/client-approve`, { satisfaction });
                        toast.success('Project approved!');
                        if (onRefresh) onRefresh();
                      } catch { toast.error('Failed'); }
                      finally { setSurveySubmitting(false); }
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {project.revisions_requested && (
            <div className="space-y-2">
              <div className="p-2 rounded bg-ug-surface/60 text-center"><span className="font-sans text-[11px] font-medium text-slate-400">Revisions Requested</span></div>
              {/* Secondary, understated "approve anyway" path (parity with
                  VideoReview) — quiet by design; confirm dialog -> existing
                  approve flow (survey -> client-approve). Shared dialog component. */}
              {!project.client_approved && (
                showSurvey ? (
                  <div className="p-4 rounded-xl bg-ug-raised border border-ug-border">
                    <SatisfactionSurvey
                      submitting={surveySubmitting}
                      onCancel={() => setShowSurvey(false)}
                      onSubmit={async (satisfaction) => {
                        setSurveySubmitting(true);
                        try {
                          await api.put(`/projects/${project.id}/client-approve`, { satisfaction });
                          toast.success('Project approved!');
                          if (onRefresh) onRefresh();
                        } catch { toast.error('Failed'); }
                        finally { setSurveySubmitting(false); }
                      }}
                    />
                  </div>
                ) : (
                  <button onClick={() => setShowApproveAnyway(true)} data-testid="approve-anyway-btn"
                    className="w-full text-center text-[10px] text-slate-500 hover:text-slate-300 underline underline-offset-2 transition-colors">
                    Changed your mind? Approve anyway
                  </button>
                )
              )}
              <ApproveAnywayDialog
                open={showApproveAnyway}
                onCancel={() => setShowApproveAnyway(false)}
                onConfirm={() => { setShowApproveAnyway(false); setShowSurvey(true); }}
              />
            </div>
          )}
          {project.client_approved && (
            <div className="p-2 rounded bg-[#1DA34E]/10 text-center"><span className="font-sans text-[11px] font-medium text-[#1DA34E]">Approved</span></div>
          )}
        </div>
      )}
      {/* Admin/editor: read-only general note */}
      {!isClient && photoSet?.general_note && (
        <div className="space-y-1 pt-3" data-testid="general-note-section">
          <h4 className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">Client General Notes</h4>
          <div className="p-2.5 rounded bg-ug-surface/50 border border-ug-border-subtle max-w-xl">
            <p className="text-[11px] text-zinc-100/70 leading-relaxed">{photoSet.general_note}</p>
          </div>
        </div>
      )}

      {/* Photo grid */}
      {/* Footage/Photo Report */}
      {showReportForm && user.role === 'editor' && (
        <div className="mb-3">
          <FootageReportForm projectId={project.id} projectType="photo" onSubmitted={() => {
            setShowReportForm(false);
            api.get(`/projects/${project.id}/footage-report`).then(r => setFootageReport(r.data?.report || null)).catch(() => {});
          }} />
        </div>
      )}
      {(user.role === 'admin' || user.role === 'staff') && footageReport && (
        <div className="mb-3">
          <FootageReportAdmin report={footageReport} projectId={project.id} onUpdate={() => {
            api.get(`/projects/${project.id}/footage-report`).then(r => setFootageReport(r.data?.report || null)).catch(() => {});
          }} />
        </div>
      )}
      {user.role === 'client' && footageReport && (
        <div className="mb-3"><FootageReportClient report={footageReport} /></div>
      )}

      <PhotoGrid photos={photos} thumbUrls={thumbUrls} onSelect={setLightboxIdx} showFlaggedOnly={showFlaggedOnly} showRevisedOnly={showRevisedOnly} isClient={isClient} maxPhotos={onFullscreen ? 25 : undefined} onFullscreen={onFullscreen}
        onDeletePhoto={isUploader ? async (photoId, filename) => {
          const ok = await confirm(`Remove "${filename}" from the gallery?`, { title: 'Delete photo?', confirmLabel: 'Remove' });
          if (!ok) return;
          try { await api.delete(`/photo-review/${project.id}/photos/${photoId}`); toast.success('Photo removed'); fetchData(); } catch { toast.error('Failed'); }
        } : null} />

      {/* Lightbox — portaled to body to escape fullscreen overlay stacking context */}
      {lightboxIdx >= 0 && createPortal(
        <Lightbox
          photos={photos.map(p => ({ ...p, project_id: project.id }))}
          thumbUrls={thumbUrls}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          onFlag={handleFlag}
          isClient={isClient}
          canDownload={canDownload}
          notesLocked={notesLocked}
          onDelete={isUploader ? async (photoId, filename) => {
            const ok = await confirm(`Remove "${filename}" from the gallery?`, { title: 'Delete photo?', confirmLabel: 'Remove' });
            if (!ok) return;
            try { await api.delete(`/photo-review/${project.id}/photos/${photoId}`); toast.success('Photo removed'); setLightboxIdx(-1); fetchData(); } catch { toast.error('Failed'); }
          } : null}
        />,
        document.body
      )}
    </div>
  );
}
